import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { INVOICE_KEYS, NIFTY_APPLICATIONID_KEY } from '@app/app.constants';
import { PatientForm } from '@app/modules/patient-forms/patient-forms.types';
import { CompletePatientFormAction } from '@app/modules/patient-forms/state/patient-forms.actions';
import { PatientFormsState } from '@app/modules/patient-forms/state/patient-forms.state';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { ServiceDataFormAdditionalPatientInformationComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-form-additional-patient-information/service-data-form-additional-patient-information.component';
import { ServiceDataFormCurrentPregnancyInformationComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-form-current-pregnancy-information/service-data-form-current-pregnancy-information.component';
import { ServiceDataFormPatientInformationComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-form-patient-information/service-data-form-patient-information.component';
import { ServiceDataFormPaymentInformationComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-form-payment-information/service-data-form-payment-information.component';
import { ServiceDataPatientHistoryInformationComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-patient-history-information/service-data-patient-history-information.component';
import {
  getDateOnlyMoment,
  getFieldSet,
  handleValidators,
  ValidatorsHandlerType,
} from '@app/modules/service-data/service-data.utils';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import {
  CreateServiceDataAction,
  GetServiceDataAction,
  UpdateServiceDataAction,
} from '@app/modules/service-data/store/service-data.actions';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import {
  FraternitySharedParent,
  ParentingTestType,
  ParticipantRelationship,
  SameBiologicalMotherAnswer,
} from '@app/modules/service-data/types/paternity.types';
import {
  DiseaseInformation,
  Ethnicity,
  FamilyDiseaseInformation,
  Gender,
} from '@app/modules/service-data/types/service-data.types';
import {
  DataInputType,
  IncidentalFindingsReporting,
  ModalMode,
  Partner,
  PaymentType,
  Product,
} from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { ProductUtilsService } from '@core/services/product-utils.service';
import { CountriesState } from '@core/store/countries/countries.state';
import { PartnersState } from '@core/store/partners/partners.state';
import { AddInvoiceAction } from '@core/store/payments/invoice-payments.actions';
import { ProductsListState } from '@core/store/products/products.state';
import { Util } from '@core/utils/core.util';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { environment } from '@root/environments/environment';
import { crossFieldValidator } from '@shared/validators/cross-field-validator';
import moment from 'moment';
import { combineLatest, of } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { createPaternityParticipantGroup } from './service-data-form-additional-participants/paternity-participant.form';
import { ServiceDataFormAdditionalParticipantsComponent } from './service-data-form-additional-participants/service-data-form-additional-participants.component';
import { ServiceDataFormBundleInformationComponent } from './service-data-form-bundle-information/service-data-form-bundle-information.component';
import { ServiceDataFormOtherInformationComponent } from './service-data-form-other-information/service-data-form-other-information.component';
import { ServiceDataFormServiceInformationComponent } from './service-data-form-service-information/service-data-form-service-information.component';
import { prefillData } from './service-data-prefill';

@Component({
  templateUrl: './service-data-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TranslateModule,
    ServiceDataFormPaymentInformationComponent,
    ServiceDataPatientHistoryInformationComponent,
    ServiceDataFormCurrentPregnancyInformationComponent,
    ServiceDataFormOtherInformationComponent,
    ServiceDataFormBundleInformationComponent,
    ServiceDataFormServiceInformationComponent,
    ServiceDataFormPatientInformationComponent,
    ServiceDataFormAdditionalPatientInformationComponent,
    ServiceDataFormAdditionalParticipantsComponent,
  ],
})
export class ServiceDataFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private cdRef = inject(ChangeDetectorRef);
  modalRef = inject(NgbActiveModal);
  private productUtils = inject(ProductUtilsService);
  private authService = inject(AccountUtilsService);
  private destroyRef = inject(DestroyRef);
  private serviceDataUtils = inject(ServiceDataUtilsService);

  @Input() public id: string;
  @Input() public patientFormId: string;

  selectedDataInputTypes: DataInputType[] = [];
  loading = false;

  protected isStaging = environment.stagingFavIcon;
  protected canViewPatientInformationSection$ = combineLatest([
    this.authService.hasRequiredPermission$('patientInformation'),
    this.authService.hasRequiredPermission$('medicalInformation'),
  ]).pipe(
    map(
      ([hasPatientInformation, hasMedicalInformation]) =>
        !this.id || hasPatientInformation || hasMedicalInformation
    )
  );

  protected prefillDisabled = signal(true);
  private prefilledPatientAddressCountry: string;
  protected fieldSet: string[] = [];

  protected form: FormGroup = this.fb.group(
    {
      id: [null],
      partnerId: this.fb.nonNullable.control('', [Validators.required]),
      bundleIds: [[], [Validators.required]],
      onlineReports: [null],
      reportLanguageId: [null, [Validators.required]],
      notes: [''],
      paymentInformation: this.fb.group({
        paymentType: [{ value: null, disabled: true }, Validators.required],
        invoiceId: [],
        paid: [],
        geneplanetInvoiceNumber: [''],
        doctorInvoiceNumber: [''],
        laboratoryInvoiceNumber: [''],
        notes: [''],
      }),
      patientInformation: this.fb.group({
        firstName: [
          '',
          [Validators.required, Validators.pattern(/^(?!.*[\\/:*?"<>|]).*$/)],
        ],
        lastName: [
          '',
          [Validators.required, Validators.pattern(/^(?!.*[\\/:*?"<>|]).*$/)],
        ],
        documentNumber: [''],
        dateOfBirth: [null, Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phoneNumber: ['', Validators.required],
      }),
      serviceInformation: this.fb.group({
        nipt: this.fb.group({
          currentPregnancyInformation: this.fb.group({
            priorScreeningTest: this.fb.group({
              priorScreeningTestType: [null],
              firstTRisks: this.fb.group({
                t21Risk: [null],
                t18Risk: [null],
                t13Risk: [null],
              }),
              secondTRisks: this.fb.group({
                t21Risk: [null],
                t18Risk: [null],
                t13Risk: [null],
              }),
            }),
          }),
          provideGenderInformation: [null],
          incidentalFindings: [null],
          comments: [''],
        }),
        mono: this.fb.group({
          monogenicIncidentalFindings: [null],
          patientHistory: this.fb.group({
            monogenicDisorderCarrier: this.fb.group({
              isCarrier: [null],
            }),
          }),
        }),
        rhd: this.fb.group({
          patientHistory: this.fb.group({
            rhdStatus: this.fb.group({
              status: [null],
            }),
          }),
        }),
        carrier: this.fb.group({
          patientHistory: this.fb.group({
            cysticFibrosisHistory: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<FamilyDiseaseInformation[]>([]),
            }),
          }),
        }),
        cancerScreen: this.fb.group({
          patientHistory: this.fb.group({
            diseaseHistory: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<DiseaseInformation[]>([]),
            }),
            familyCancerHistory: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<FamilyDiseaseInformation[]>([]),
            }),
            knownFamilyMutations: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<FamilyDiseaseInformation[]>([]),
            }),
          }),
        }),
        galleri: this.fb.group({
          currentPregnancyInformation: this.fb.group({
            isPregnant: [null],
          }),
          patientHistory: this.fb.group({
            currentlyDiagnosedWithCancer: [null],
            cancerSuspicion: this.fb.group({
              isSuspicious: [null],
              suspicionDescription: [null],
            }),
            cancerSurvivor: this.fb.group({
              isSurvivor: [null],
              moreThanThreeYearsAgo: [null],
            }),
            solidOrganTransplant: this.fb.group({
              hasReceived: [null],
            }),
          }),
        }),
        nippt: this.fb.group({
          allegedFather: this.fb.group({
            firstName: [''],
            lastName: [''],
            documentNumber: [''],
            dateOfBirth: [null],
            email: [''],
            phoneNumber: [''],
            // Select value; "Other" ethnicities are free text in `ethnicity`
            ethnicitySelect: this.fb.control<Ethnicity>(null),
            ethnicity: this.fb.control<string>(null),
          }),
          patientHistory: this.fb.group({
            anotherPregnancy: this.fb.group({
              wasPregnant: [null],
              type: [null],
              date: [null],
            }),
            anyReceivedStemCell: [null],
            anyReceivedBloodTransfusion: [null],
          }),
        }),
        paternity: this.fb.group({
          parentingTestType: this.fb.control<ParentingTestType>(null),
          // The patient is additionalParticipants[0], but is never shown as
          // an additional participant. Their id is kept to link their sample.
          patientParticipantId: this.fb.control<string>(null),
          relationship: this.fb.control<ParticipantRelationship>(null),
          additionalParticipants: this.fb.array<FormGroup>([]),
          patientHistory: this.fb.group({
            isSameBiologicalMother:
              this.fb.control<SameBiologicalMotherAnswer>(null),
            wantToKnow: this.fb.control<FraternitySharedParent>(null),
          }),
        }),
        venisafe: this.fb.group({
          patientHistory: this.fb.group({
            cardiovascularConditions: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<DiseaseInformation[]>([]),
            }),
            cardiovascularConditionsInFamily: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<FamilyDiseaseInformation[]>([]),
            }),
          }),
        }),
        shared: this.fb.group({
          patientGender: this.fb.control<Gender>(null),
          doctorInformation: this.fb.group({
            doctorId: [{ value: null, disabled: true }, Validators.required],
            doctorConsent: [null],
          }),
          patientInformation: this.fb.group({
            documentNumber: [''],
            height: [null],
            weight: [null],
            // Select value; "Other" ethnicities are free text in `ethnicity`
            ethnicitySelect: this.fb.control<Ethnicity>(null),
            ethnicity: this.fb.control<string>(null),
            address: this.fb.group({
              street: this.fb.control<string>(null),
              city: this.fb.control<string>(null),
              zip: this.fb.control<string>(null),
              country: this.fb.control<string>(null),
            }),
          }),
          patientHistory: this.fb.group({
            numberOfPregnancies: [null],
            numberOfBirths: [null],
            cancerHistory: this.fb.group({
              hasHistory: [null],
              status: [null],
            }),
            albuminTherapy: this.fb.group({
              hasReceived: [null],
              isRelevant: [null],
            }),
            cellularImmunotherapy: this.fb.group({
              hasReceived: [null],
              isRelevant: [null],
            }),
            stemCellTherapy: this.fb.group({
              hasReceived: [null],
            }),
            transplantSurgery: this.fb.group({
              hasReceived: [null],
            }),
            bloodTransfusion: this.fb.group({
              hasReceived: [null],
              isRelevant: [null],
            }),
            familyDiseaseHistory: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<FamilyDiseaseInformation[]>([]),
            }),
            pregnancyDiseaseHistory: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<DiseaseInformation[]>([]),
            }),
            carrierOfGeneticDiseases: this.fb.group({
              hasHistory: [null],
              diseases: this.fb.control<DiseaseInformation[]>([]),
            }),
            chromosomalAbnormalityCarrier: this.fb.group({
              isCarrier: [null],
            }),
            boneMarrowTransplant: this.fb.group({
              hasReceived: [null],
            }),
          }),
          currentPregnancyInformation: this.fb.group({
            expectedConfinementDate: [null],
            gestationAge: this.fb.group({
              week: [null],
              day: [null],
            }),
            isGestationAgeAutomaticallyCalculated: [true],
            pregnancyType: [null],
            chorionicityType: [null],
            inVitro: this.fb.group({
              isInVitroFertilized: [null],
              vitroType: [null],
              isSurrogateMother: [null],
            }),
            vanishingTwinSyndrome: this.fb.group({
              isDetected: [null],
              detectedGestationAge: this.fb.group({
                week: [null],
                day: [null],
              }),
            }),
            heparinTherapy: this.fb.group({
              hasReceived: [null],
              isRelevant: [null],
            }),
            ultrasoundDate: [null],
          }),
          comments: [''],
        }),
      }),
    },
    {
      validators: [
        crossFieldValidator(
          'paymentInformation.notes',
          'paymentInformation.paymentType',
          [PaymentType.Customer1, PaymentType.Customer2]
        ),
      ],
    }
  );

  ngOnInit() {
    if (this.id) {
      this.store
        .dispatch(new GetServiceDataAction(this.id))
        .pipe(
          switchMap(() =>
            this.store.select(ServiceDataState.getServiceDataById)
          ),
          map((getById) => getById(this.id)),
          switchMap((serviceData) =>
            combineLatest([
              of(serviceData),
              this.setInputTypes(serviceData.bundleIds),
            ])
          ),
          first()
        )
        .subscribe(([serviceData]) => {
          const formData = this.serviceDataUtils.serviceDataToForm(
            serviceData,
            this.selectedDataInputTypes
          );
          formData.serviceInformation.shared.currentPregnancyInformation.isGestationAgeAutomaticallyCalculated = false;

          // FormArray controls must exist before patching
          this.setParticipantsCount(
            formData.serviceInformation.paternity?.additionalParticipants
              ?.length ?? 0
          );
          this.form.patchValue(formData);
          this.applyCarrierQuestionCompatibility();

          if (serviceData?.sampleIds && serviceData.sampleIds.length) {
            this.form.get('bundleIds').disable();
          }

          this.cdRef.markForCheck();
        });
    } else if (this.patientFormId) {
      this.store
        .select(PatientFormsState.getPatientFormById)
        .pipe(
          first(),
          map((getPatientFormById) => getPatientFormById(this.patientFormId))
        )
        .subscribe((patientForm: PatientForm) => {
          this.patchFormWithPatientFormData(patientForm);
          this.applyCarrierQuestionCompatibility();
          this.form.markAllAsTouched();
          this.cdRef.markForCheck();
        });
    }

    this.form
      .get('partnerId')
      .valueChanges.pipe(
        switchMap((partnerId) =>
          this.store
            .select(PartnersState.getPartnerById)
            .pipe(map((getById) => getById(partnerId)))
        )
      )
      .subscribe((partner) => {
        const country = this.store.selectSnapshot(
          CountriesState.getCountryByName
        )(partner?.address?.country);
        this.form.patchValue({
          onlineReports:
            partner?.onlineReport ?? country?.onlineReport ?? false,
          reportLanguageId: this.getDefaultReportLanguageId(partner),
        });
        this.prefillPatientAddressCountry(partner?.address?.country);
      });
    this.form
      .get('serviceInformation.shared.patientGender')
      .valueChanges.pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((gender: Gender) => this.handleGalleriPregnancyStatus(gender));
    this.form
      .get('bundleIds')
      .valueChanges.pipe(
        startWith(this.form.get('bundleIds').value),
        takeUntilDestroyed(this.destroyRef),
        filter((bundles) => !!bundles),
        tap((bundles: string[]) => {
          this.prefillDisabled.set(!bundles?.length);
        }),
        switchMap(this.setInputTypes.bind(this))
      )
      .subscribe((products) => {
        this.handlePregnancyType(products);
        this.handleGender(products);
        this.handlePaternityParticipants(products);
        handleValidators(
          products,
          this.form,
          'serviceInformation',
          ValidatorsHandlerType.SERVICEDATA
        );
        this.handlePatientAddress(products);

        this.applyIncidentalFindingsReporting(
          products.filter(
            (product) => product.dataInputType === DataInputType.NIPT
          ),
          this.form.get(
            'serviceInformation.nipt.incidentalFindings'
          ) as FormControl
        );
        this.applyIncidentalFindingsReporting(
          products.filter(
            (product) => product.dataInputType === DataInputType.MONO
          ),
          this.form.get(
            'serviceInformation.mono.monogenicIncidentalFindings'
          ) as FormControl
        );
        this.applyCarrierQuestionCompatibility();
      });
  }

  protected setInputTypes(bundleIds: string[]) {
    return this.store.select(ProductsListState.getBundles).pipe(
      map((getByIds) => getByIds(bundleIds)),
      switchMap((bundles) =>
        this.store
          .select(ProductsListState.getAllChildProducts)
          .pipe(
            map((getByIds) =>
              getByIds(bundles.map((bundle) => bundle.productIds).flat(1))
            )
          )
      ),
      tap((products) => {
        this.selectedDataInputTypes = products.map(
          (product) => product.dataInputType
        );
        this.fieldSet = getFieldSet(
          this.selectedDataInputTypes,
          ValidatorsHandlerType.SERVICEDATA
        );
        // The patient address is asked for any product that requires it,
        // regardless of the data input type
        if (this.isPatientAddressRequired(products)) {
          this.fieldSet = [
            ...new Set([
              ...this.fieldSet,
              ServiceDataFields.AdditionalPatientInformation,
              ServiceDataFields.PatientAddress,
            ]),
          ];
        }
      })
    );
  }

  protected prefillTestData() {
    this.form.patchValue(prefillData);

    this.cdRef.markForCheck();
  }

  protected submit() {
    this.form.markAllAsTouched();

    const formData = this.form.getRawValue();

    const serviceData = this.serviceDataUtils.formToServiceData(
      formData,
      this.selectedDataInputTypes
    );

    if (this.form.valid) {
      this.loading = true;
      if (!this.id) {
        const invoiceId = Util.CreateGuid();
        serviceData.paymentInformation.invoiceId = invoiceId;
        this.store
          .dispatch(
            new AddInvoiceAction({
              applicationId: NIFTY_APPLICATIONID_KEY,
              id: invoiceId,
              properties: [
                {
                  itemKey: `${INVOICE_KEYS.SepaContract}`,
                  itemValue: 'false',
                },
              ],
              invoicePayments: [],
            })
          )
          .pipe(
            delay(200),
            switchMap(() =>
              this.store.dispatch(new CreateServiceDataAction(serviceData))
            ),
            switchMap(() =>
              this.patientFormId
                ? this.store.dispatch(
                    new CompletePatientFormAction(this.patientFormId)
                  )
                : of(null)
            )
          )
          .subscribe({
            next: () => this.modalRef.close(),
            error: () => this.onSubmitError(),
          });
      } else {
        this.store
          .dispatch(new UpdateServiceDataAction(serviceData))
          .subscribe({
            next: () => this.modalRef.close(),
            error: () => this.onSubmitError(),
          });
      }
    }
  }

  // The error interceptor already shows the API error; keep the form open
  // so the user can fix the data
  private onSubmitError() {
    this.loading = false;
    this.cdRef.markForCheck();
  }

  private handlePregnancyType(products: Product[]) {
    const types = this.productUtils.getBundlesNumberOfFetuses(products);
    if (types.length === 1) {
      this.form
        .get(
          'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
        )
        .setValue(types[0]);
      this.form
        .get(
          'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
        )
        .disable();
    } else if (types.length > 1) {
      this.form
        .get(
          'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
        )
        .enable();
    } else {
      this.form
        .get(
          'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
        )
        .setValue(null);
      if (this.id) {
        this.form
          .get(
            'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
          )
          .disable();
      } else {
        this.form
          .get(
            'serviceInformation.shared.currentPregnancyInformation.pregnancyType'
          )
          .enable();
      }
    }
  }

  private handleGalleriPregnancyStatus(gender: Gender) {
    const isPregnant = this.form.get(
      'serviceInformation.galleri.currentPregnancyInformation.isPregnant'
    );
    if (gender === Gender.Male) {
      isPregnant.setValue(false);
      isPregnant.disable();
    } else if (isPregnant.disabled) {
      isPregnant.setValue(null);
      isPregnant.enable();
    }
  }

  private handleGender(products: Product[]) {
    const types = this.productUtils.getBundlesGender(products);
    if (types.length === 1) {
      this.form
        .get('serviceInformation.shared.patientGender')
        .setValue(types[0]);
      this.form.get('serviceInformation.shared.patientGender').disable();
    } else if (types.length > 1) {
      this.form.get('serviceInformation.shared.patientGender').enable();
    } else {
      this.form.get('serviceInformation.shared.patientGender').setValue(null);
      if (this.id) {
        this.form.get('serviceInformation.shared.patientGender').disable();
      } else {
        this.form.get('serviceInformation.shared.patientGender').enable();
      }
    }
  }

  // As many participants as Paternity products (samples) in the bundle.
  // The patient is participant 1, the rest are additional participants.
  // Participants are not tied to products, that happens at sample creation.
  private handlePaternityParticipants(products: Product[]) {
    const paternityProducts = products.filter(
      (product) => product.dataInputType === DataInputType.PATERNITY
    );

    this.setParticipantsCount(Math.max(paternityProducts.length - 1, 0));
  }

  private handlePatientAddress(products: Product[]) {
    if (!this.isPatientAddressRequired(products)) {
      return;
    }

    const address = this.form.get(
      'serviceInformation.shared.patientInformation.address'
    ) as FormGroup;
    Object.values(address.controls).forEach((control) => {
      control.addValidators(Validators.required);
      control.updateValueAndValidity();
    });
  }

  // The patient address country defaults to the partner's country, but stays
  // editable. A country the user changed is kept when the partner changes.
  private prefillPatientAddressCountry(partnerCountry: string) {
    const countryControl = this.form.get(
      'serviceInformation.shared.patientInformation.address.country'
    );

    if (
      !countryControl.value ||
      countryControl.value === this.prefilledPatientAddressCountry
    ) {
      countryControl.setValue(partnerCountry ?? null);
    }
    this.prefilledPatientAddressCountry = partnerCountry;
  }

  private isPatientAddressRequired(products: Product[]) {
    return products.some((product) => product?.requirePatientAddress);
  }

  private setParticipantsCount(count: number) {
    const participants = this.form.get(
      'serviceInformation.paternity.additionalParticipants'
    ) as FormArray<FormGroup>;

    while (participants.length > count) {
      participants.removeAt(participants.length - 1);
    }
    while (participants.length < count) {
      participants.push(createPaternityParticipantGroup());
    }
  }

  private applyIncidentalFindingsReporting(
    products: Product[],
    formControl: FormControl
  ) {
    const currentValue = formControl.value;
    const reportingModes = products
      .map((product) => product.incidentalFindingsReporting)
      .filter((reporting) => !!reporting);

    if (reportingModes.includes(IncidentalFindingsReporting.Always)) {
      formControl.setValue(true);
      formControl.disable();
    } else if (reportingModes.includes(IncidentalFindingsReporting.Ask)) {
      formControl.enable();
      formControl.setValue(
        typeof currentValue === 'boolean' ? currentValue : null
      );
    } else {
      formControl.setValue(null);
      formControl.disable();
    }
  }

  private applyCarrierQuestionCompatibility() {
    const legacyCarrier = this.form.get(
      'serviceInformation.shared.patientHistory.carrierOfGeneticDiseases'
    );
    const chromosomalCarrier = this.form.get(
      'serviceInformation.shared.patientHistory.chromosomalAbnormalityCarrier'
    );
    const monogenicCarrier = this.form.get(
      'serviceInformation.mono.patientHistory.monogenicDisorderCarrier'
    );

    const legacyHasHistory = legacyCarrier?.get('hasHistory')?.value;
    const hasLegacyValue =
      legacyHasHistory !== null && legacyHasHistory !== undefined;

    this.setControlEnabled(
      legacyCarrier,
      hasLegacyValue &&
        this.fieldSet.includes(ServiceDataFields.CarrierOfGeneticCondition)
    );
    this.setControlEnabled(
      chromosomalCarrier,
      !hasLegacyValue &&
        this.fieldSet.includes(ServiceDataFields.ChromosomalAbnormalityCarrier)
    );
    this.setControlEnabled(
      monogenicCarrier,
      !hasLegacyValue &&
        this.fieldSet.includes(ServiceDataFields.MonogenicDisorderCarrier)
    );
  }

  private setControlEnabled(control: AbstractControl | null, enabled: boolean) {
    if (!control) {
      return;
    }

    if (enabled && control.disabled) {
      control.enable({ emitEvent: false });
    } else if (!enabled && control.enabled) {
      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  protected readonly ModalMode = ModalMode;

  private patchFormWithPatientFormData(data: PatientForm) {
    this.form.patchValue({
      partnerId: data.partnerId,
      bundleIds: [data.productId],
      patientInformation: {
        ...data.patientInformation,
        email: data.email,
        dateOfBirth: getDateOnlyMoment(data.patientInformation?.dateOfBirth),
        phoneNumber: data.patientInformation?.telephoneNumber,
      },
      serviceInformation: {
        nipt: {
          ...data.additionalInformation,
          provideGenderInformation:
            data.additionalInformation?.genderInformation,
          currentPregnancyInformation: {
            priorScreeningTest: {
              priorScreeningTestType:
                data.currentPregnancy?.priorScreeningTestType,
              firstTRisks: {
                t21Risk: data.currentPregnancy?.firstT21Risk,
                t18Risk: data.currentPregnancy?.firstT18Risk,
                t13Risk: data.currentPregnancy?.firstT13Risk,
              },
              secondTRisks: {
                t21Risk: data.currentPregnancy?.secondT21Risk,
                t18Risk: data.currentPregnancy?.secondT18Risk,
                t13Risk: data.currentPregnancy?.secondT13Risk,
              },
            },
          },
        },
        mono: {
          monogenicIncidentalFindings:
            data.additionalInformation?.monogenicIncidentalFindings,
          patientHistory: {
            monogenicDisorderCarrier: {
              isCarrier:
                data.historyInformation?.monogenicDisorderCarrierIsCarrier,
            },
          },
        },
        rhd: {
          patientHistory: {
            rhdStatus: {
              status: data.historyInformation?.rhdStatus,
            },
          },
        },
        carrier: {
          patientHistory: {
            cysticFibrosisHistory: {
              hasHistory: data.historyInformation?.cysticFibrosisHistory,
              diseases:
                data.historyInformation?.cysticFibrosisHistoryCondition &&
                data.historyInformation?.cysticFibrosisHistoryRelation
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation
                            .cysticFibrosisHistoryCondition,
                        relationToPatient:
                          data.historyInformation.cysticFibrosisHistoryRelation,
                      },
                    ]
                  : [],
            },
          },
        },
        cancerScreen: {
          patientHistory: {
            diseaseHistory: {
              hasHistory: data.historyInformation?.diseaseHistory,
              diseases:
                data.historyInformation?.diseaseHistoryCondition &&
                data.historyInformation?.diseaseHistoryAgeOfOnset
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation.diseaseHistoryCondition,
                        ageOfOnset:
                          data.historyInformation.diseaseHistoryAgeOfOnset,
                      },
                    ]
                  : [],
            },
            familyCancerHistory: {
              hasHistory: data.historyInformation?.familyCancerHistory,
              diseases:
                data.historyInformation?.familyCancerHistoryCondition &&
                data.historyInformation?.familyCancerHistoryRelation &&
                data.historyInformation?.familyCancerHistoryAgeOfOnset
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation.familyCancerHistoryCondition,
                        relationToPatient:
                          data.historyInformation.familyCancerHistoryRelation,
                        ageOfOnset:
                          data.historyInformation.familyCancerHistoryAgeOfOnset,
                      },
                    ]
                  : [],
            },
            knownFamilyMutations: {
              hasHistory: data.historyInformation?.knownFamilyMutation,
              diseases:
                data.historyInformation?.knownFamilyMutationCondition &&
                data.historyInformation?.knownFamilyMutationRelation &&
                data.historyInformation?.knownFamilyMutationAgeOfOnset
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation.knownFamilyMutationCondition,
                        relationToPatient:
                          data.historyInformation.knownFamilyMutationRelation,
                        ageOfOnset:
                          data.historyInformation.knownFamilyMutationAgeOfOnset,
                      },
                    ]
                  : [],
            },
          },
        },
        galleri: {
          currentPregnancyInformation: {
            isPregnant: data.currentPregnancy?.isPregnant,
          },
          patientHistory: {
            currentlyDiagnosedWithCancer:
              data.historyInformation?.currentlyDiagnosedWithCancer,
            cancerSuspicion: {
              isSuspicious:
                data.historyInformation?.cancerSuspicionIsSuspicious,
              suspicionDescription:
                data.historyInformation?.cancerSuspicionDescription,
            },
            cancerSurvivor: {
              isSurvivor: data.historyInformation?.cancerSurvivorIsSurvivor,
              moreThanThreeYearsAgo:
                data.historyInformation?.cancerSurvivorMoreThanThreeYearsAgo,
            },
            solidOrganTransplant: {
              hasReceived:
                data.historyInformation?.solidOrganTransplantHasReceived,
            },
          },
        },
        venisafe: {
          patientHistory: {
            cardiovascularConditions: {
              hasHistory:
                data.historyInformation?.cardiovascularConditionHistory,
              diseases: data.historyInformation
                ?.cardiovascularConditionHistoryCondition
                ? [
                    {
                      conditionDescription:
                        data.historyInformation
                          .cardiovascularConditionHistoryCondition,
                    },
                  ]
                : [],
            },
            cardiovascularConditionsInFamily: {
              hasHistory:
                data.historyInformation?.familyCardiovascularConditionHistory,
              diseases:
                data.historyInformation
                  ?.familyCardiovascularConditionHistoryCondition &&
                data.historyInformation
                  ?.familyCardiovascularConditionHistoryRelation
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation
                            .familyCardiovascularConditionHistoryCondition,
                        relationToPatient:
                          data.historyInformation
                            .familyCardiovascularConditionHistoryRelation,
                      },
                    ]
                  : [],
            },
          },
        },
        shared: {
          patientGender: data.patientInformation?.patientGender,
          doctorInformation: {
            doctorId: data?.doctorId,
          },
          patientInformation: {
            ...data.patientInformation,
            ethnicitySelect: data.patientInformation?.ethnicity,
          },
          currentPregnancyInformation: {
            ...data.currentPregnancy,
            expectedConfinementDate: data.currentPregnancy?.expDateOfConfinement
              ? moment(data.currentPregnancy.expDateOfConfinement)
              : null,
            ultrasoundDate: data.currentPregnancy?.dateOfUltrasound
              ? moment(data.currentPregnancy.dateOfUltrasound)
              : null,
            inVitro: {
              isInVitroFertilized: data.currentPregnancy?.inVitro,
              vitroType: data.currentPregnancy?.vitroType,
              isSurrogateMother: data.currentPregnancy?.isSurrogateMother,
            },
            vanishingTwinSyndrome: {
              isDetected: data.currentPregnancy?.vanishingTwinSyndrome,
            },
            heparinTherapy: {
              hasReceived: data.currentPregnancy?.heparinTherapy,
            },
          },
          patientHistory: {
            numberOfPregnancies: data.historyInformation?.gravida,
            numberOfBirths: data.historyInformation?.para,
            cancerHistory: {
              hasHistory: data.historyInformation?.historyOfCancer,
            },
            albuminTherapy: {
              hasReceived: data.historyInformation?.albuminTherapy,
            },
            cellularImmunotherapy: {
              hasReceived: data.historyInformation?.cellularImmunotherapy,
            },
            stemCellTherapy: {
              hasReceived: data.historyInformation?.stemCellTherapy,
            },
            transplantSurgery: {
              hasReceived: data.historyInformation?.transplantSurgery,
            },
            bloodTransfusion: {
              hasReceived: data.historyInformation?.bloodTransfusion,
            },
            familyDiseaseHistory: {
              hasHistory: data.historyInformation?.familyDiseaseHistory,
              diseases:
                data.historyInformation?.familyDiseaseHistoryCondition &&
                data.historyInformation?.familyDiseaseHistoryRelation
                  ? [
                      {
                        conditionDescription:
                          data.historyInformation.familyDiseaseHistoryCondition,
                        relationToPatient:
                          data.historyInformation.familyDiseaseHistoryRelation,
                      },
                    ]
                  : [],
            },
            pregnancyDiseaseHistory: {
              hasHistory: data.historyInformation?.pregnancyDiseaseHistory,
              diseases: data.historyInformation
                ?.pregnancyDiseaseHistoryCondition
                ? [
                    {
                      conditionDescription:
                        data.historyInformation
                          .pregnancyDiseaseHistoryCondition,
                    },
                  ]
                : [],
            },
            carrierOfGeneticDiseases: {
              hasHistory:
                data.historyInformation?.carrierOfGeneticDiseaseInformations,
              diseases: data.historyInformation
                ?.carrierOfGeneticDiseaseInformationsCondition
                ? [
                    {
                      conditionDescription:
                        data.historyInformation
                          .carrierOfGeneticDiseaseInformationsCondition,
                    },
                  ]
                : [],
            },
            chromosomalAbnormalityCarrier: {
              isCarrier:
                data.historyInformation?.chromosomalAbnormalityCarrierIsCarrier,
            },
            boneMarrowTransplant: {
              hasReceived:
                data.historyInformation?.boneMarrowTransplantHasReceived,
            },
          },
          comments: data.additionalInformation?.comments,
        },
      },
    });
  }

  private getDefaultReportLanguageId(partner: Partner): string | null {
    if (!partner) {
      return null;
    }

    if (partner.reportLanguageIds?.length === 1) {
      return partner.reportLanguageIds[0];
    }

    if (!partner.reportLanguageIds?.length) {
      return partner.languageIds[0] ?? null;
    }

    const preferredLanguageId = partner.languageIds[0];
    return preferredLanguageId &&
      partner.reportLanguageIds.includes(preferredLanguageId)
      ? preferredLanguageId
      : null;
  }
}
